import { RefObject, useEffect } from 'react';

const INPUT_SELECTOR = 'input[role="combobox"][aria-expanded="true"]';
const SHOW_ALL_SELECTOR = '[data-testid="location-search-show-all-button"]';
const ACTIVE_OPTION_SELECTOR = 'li[role="option"].Mui-focused';

export const useShowAllKeys = (
  open: boolean,
  focusTransferReference: RefObject<boolean>,
) => {
  useEffect(() => {
    if (!open) return;
    const onShowAllFocusIn = (focusEvent: FocusEvent) => {
      const targetElement = focusEvent.target as Element | null;
      if (targetElement && targetElement.closest(SHOW_ALL_SELECTOR)) {
        const optionElement = document.querySelector<HTMLLIElement>(
          ACTIVE_OPTION_SELECTOR,
        );
        optionElement?.classList.remove('Mui-focused');
        optionElement?.classList.remove('Mui-focusVisible');
      }
    };
    document.addEventListener('focusin', onShowAllFocusIn, true);
    return () =>
      document.removeEventListener('focusin', onShowAllFocusIn, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const firstOptionInNextGroup = (buttonElement: HTMLElement) => {
      let next: Element | null = buttonElement.closest('li');
      while (next && (next = next.nextElementSibling)) {
        const li = next.querySelector<HTMLLIElement>('li[role="option"]');
        if (li) return li;
      }
      return null;
    };

    const lastOptionInCurrentGroup = (buttonElement: HTMLElement) => {
      let prev = buttonElement.previousElementSibling;
      while (prev && prev.tagName !== 'UL') prev = prev.previousElementSibling;
      if (!(prev instanceof HTMLUListElement)) return null;

      const items = prev.querySelectorAll<HTMLLIElement>('li[role="option"]');
      return items.length > 0 ? items.item(items.length - 1) : null;
    };

    const onKeyFromShowAll = (event: KeyboardEvent) => {
      const showAllButtonElement = (event.target as Element | null)?.closest(
        SHOW_ALL_SELECTOR,
      );
      if (!showAllButtonElement) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const targetOptionElement =
        event.key === 'ArrowDown'
          ? firstOptionInNextGroup(showAllButtonElement as HTMLElement)
          : lastOptionInCurrentGroup(showAllButtonElement as HTMLElement);
      if (!targetOptionElement) return;

      event.preventDefault();
      event.stopPropagation();

      focusTransferReference.current = true;

      const comboboxInputElement =
        document.querySelector<HTMLInputElement>(INPUT_SELECTOR);
      comboboxInputElement?.focus();

      requestAnimationFrame(() => {
        targetOptionElement.scrollIntoView({ block: 'nearest' });
        targetOptionElement.classList.add('Mui-focused', 'Mui-focusVisible');
        targetOptionElement.dispatchEvent(
          new MouseEvent('mousemove', { bubbles: true }),
        );

        queueMicrotask(() => {
          const hasHighlight =
            targetOptionElement.classList.contains('Mui-focused');
          if (!hasHighlight && comboboxInputElement) {
            comboboxInputElement.dispatchEvent(
              new KeyboardEvent('keydown', { key: event.key, bubbles: true }),
            );
          }
        });

        document.addEventListener(
          'focusin',
          () => (focusTransferReference.current = false),
          {
            once: true,
            capture: true,
          },
        );
      });
    };

    document.addEventListener('keydown', onKeyFromShowAll, true);
    return () =>
      document.removeEventListener('keydown', onKeyFromShowAll, true);
  }, [open, focusTransferReference]);

  useEffect(() => {
    if (!open) return;

    const openComboboxElement =
      document.querySelector<HTMLInputElement>(INPUT_SELECTOR);
    if (!openComboboxElement) return;

    const getPreviousOption = (optionElement: HTMLLIElement) => {
      let prevOptionElement = optionElement.previousElementSibling;
      while (
        prevOptionElement &&
        prevOptionElement.getAttribute('role') !== 'option'
      ) {
        prevOptionElement = prevOptionElement.previousElementSibling;
      }
      return prevOptionElement as HTMLLIElement | null;
    };

    const getNextOption = (optionElement: HTMLLIElement) => {
      let nextOptionElement = optionElement.nextElementSibling;
      while (
        nextOptionElement &&
        nextOptionElement.getAttribute('role') !== 'option'
      ) {
        nextOptionElement = nextOptionElement.nextElementSibling;
      }
      return nextOptionElement as HTMLLIElement | null;
    };

    const getCurrentGroupButton = (optionsListElement: HTMLElement) =>
      optionsListElement.parentElement?.querySelector<HTMLElement>(
        SHOW_ALL_SELECTOR,
      ) || null;

    const getPreviousGroupButton = (optionsListElement: HTMLElement) => {
      let prevGroupListItem =
        optionsListElement.parentElement?.previousElementSibling;
      while (prevGroupListItem) {
        const showAllButtonElement =
          prevGroupListItem.querySelector<HTMLElement>(SHOW_ALL_SELECTOR);
        if (showAllButtonElement) return showAllButtonElement;
        prevGroupListItem = prevGroupListItem.previousElementSibling;
      }
      return null;
    };

    const onKeyCapture = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const activeOptionElement = document.querySelector<HTMLLIElement>(
        ACTIVE_OPTION_SELECTOR,
      );
      const optionsListElement = activeOptionElement?.closest('ul');
      if (!activeOptionElement || !optionsListElement) return;

      if (event.key === 'ArrowDown') {
        if (getNextOption(activeOptionElement)) return;
        const showAllButtonElement = getCurrentGroupButton(
          optionsListElement as HTMLElement,
        );
        if (!showAllButtonElement) return;

        event.preventDefault();
        event.stopPropagation();

        focusTransferReference.current = true;
        activeOptionElement.classList.remove('Mui-focused');
        activeOptionElement.classList.remove('Mui-focusVisible');

        requestAnimationFrame(() => {
          showAllButtonElement.focus();
          document.addEventListener(
            'focusin',
            () => (focusTransferReference.current = false),
            {
              once: true,
              capture: true,
            },
          );
        });
        return;
      }

      if (event.key === 'ArrowUp') {
        if (getPreviousOption(activeOptionElement)) return;
        const showAllButtonElement = getPreviousGroupButton(
          optionsListElement as HTMLElement,
        );
        if (!showAllButtonElement) return;

        event.preventDefault();
        event.stopPropagation();

        focusTransferReference.current = true;
        activeOptionElement.classList.remove('Mui-focused');
        activeOptionElement.classList.remove('Mui-focusVisible');

        requestAnimationFrame(() => {
          showAllButtonElement.focus();
          document.addEventListener(
            'focusin',
            () => (focusTransferReference.current = false),
            {
              once: true,
              capture: true,
            },
          );
        });
      }
    };

    openComboboxElement.addEventListener('keydown', onKeyCapture, true);
    return () =>
      openComboboxElement.removeEventListener('keydown', onKeyCapture, true);
  }, [open, focusTransferReference]);
};
